import type { Issue } from "@h2lca/core";
import type { StepId } from "@/store/workshop";

/** 텍스트를 파일로 내려받는다(CSV,JSON) */
export function downloadText(filename: string, text: string, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** 파일 이름에 못 쓰는 글자를 걷어낸다 */
export function safeFileName(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, "_").trim() || "scenario";
}

/** ISO 날짜를 한국어로 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "없음";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "없음";
  return d.toLocaleString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
}

/** 검증 메시지가 가리키는 산정 단계 , 전력 믹스는 운전자료 입력 단계에 있다 */
export function stepOfIssue(issue: Issue): StepId | null {
  if (!issue.step || issue.step === "method") return null;
  return issue.step === "electricity" ? "inputs" : issue.step;
}

export const ISSUE_LEVEL_LABEL = { error: "오류", warning: "경고", info: "안내" } as const;

export const H2_STATE_LABEL = { gas: "기체", liquid: "액체" } as const;

export const DENOMINATOR_LABEL = { delivered: "최종 인도 수소량", gate: "생산시설 출하지점 수소량" } as const;

/** 배출계수 자료 상태 → 배지 문구. 마스터 상태 외에 엔진이 만드는 두 상태가 있다 */
export function efStatusLabel(status: string): { label: string; tone: "warn" | "ok" | "soft" } {
  switch (status) {
    case "placeholder":
      return { label: "임시값(전문가 검토 전)", tone: "warn" };
    case "reviewed":
      return { label: "전문가 검토 완료", tone: "ok" };
    case "confirmed":
      return { label: "발주기관 확인 완료", tone: "ok" };
    case "composed":
      return { label: "전력 믹스 합성", tone: "soft" };
    case "user":
      return { label: "사용자 정의 계수", tone: "soft" };
    default:
      return { label: status, tone: "soft" };
  }
}

/**
 * 저장 상태를 사람 말로. '저장됨' 한 단어는 무엇이 어디에 저장됐는지 말해 주지 않는다(실제 지적).
 * 세 자리가 있다 , 이 브라우저(임시 보관, 자동), 서버 보관함(직접 저장, 다른 기기에서도 열림), 내 컴퓨터 파일(내보내기).
 */
export function saveState(serverId: string | null, dirty: boolean, lastSavedAt: string | null): { tone: "ok" | "warn"; text: string } {
  if (!serverId) return { tone: "warn", text: "보관함에 저장하지 않음. 이 브라우저에만 임시 보관 중" };
  const at = formatTime(lastSavedAt);
  if (dirty) return { tone: "warn", text: `보관함 저장본${at ? `(${at})` : ""}과 다름. 바뀐 내용은 이 브라우저에만 임시 보관 중` };
  return { tone: "ok", text: at ? `보관함에 저장됨, ${at}` : "보관함에 저장됨" };
}

export const SAVE_HELP =
  "임시 보관은 이 브라우저(로컬 저장소)에만 남고, 브라우저 데이터를 지우면 사라집니다. 다른 기기에서 열거나 교육이 끝난 뒤에도 보려면 '보관함에 저장' 을 누르세요. 파일로 내려받아 두는 방법도 있습니다(내보내기).";
